import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Sidebar = ({ isCollapsed = false, onToggleCollapse }) => {
  const location = useLocation();
  const [hoveredItem, setHoveredItem] = useState(null);

  const navigationItems = [
    { 
      label: 'Dashboard', 
      path: '/dashboard-overview', 
      icon: 'LayoutDashboard',
      tooltip: 'System overview and metrics',
      roles: ['admin', 'operator', 'auditor']
    },
    { 
      label: 'VIN Consultation', 
      path: '/vin-consultation', 
      icon: 'Search',
      tooltip: 'Vehicle information lookup',
      roles: ['admin', 'operator', 'auditor']
    },
    { 
      label: 'History & Audit', 
      path: '/history-and-audit-trail', 
      icon: 'History',
      tooltip: 'Compliance and tracking',
      roles: ['admin', 'operator', 'auditor']
    },
    { 
      label: 'User Management', 
      path: '/user-administration', 
      icon: 'Users',
      tooltip: 'User lifecycle and access control',
      roles: ['admin']
    },
    { 
      label: 'System Configuration', 
      path: '/system-configuration', 
      icon: 'Settings',
      tooltip: 'Global policy and security management',
      roles: ['admin']
    },
  ];

  const isActivePath = (path) => location?.pathname === path;

  const handleItemHover = (index) => {
    if (isCollapsed) {
      setHoveredItem(index);
    }
  };

  const handleItemLeave = () => {
    setHoveredItem(null);
  };

  return (
    <>
      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 z-40 h-screen bg-card border-r border-border shadow-elevation transition-all duration-300 ${
        isCollapsed ? 'w-16' : 'w-60'
      }`}>
        {/* Logo Section */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-border">
          {!isCollapsed && (
            <Link to="/dashboard-overview" className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-lg">
                <Icon name="Car" size={20} color="white" />
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-semibold text-foreground">VIN Pro</span>
                <span className="text-xs text-muted-foreground -mt-1">Platform</span>
              </div>
            </Link>
          )}
          
          {isCollapsed && (
            <Link to="/dashboard-overview" className="flex items-center justify-center w-full">
              <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-lg">
                <Icon name="Car" size={20} color="white" />
              </div>
            </Link>
          )}

          {onToggleCollapse && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleCollapse}
              iconName={isCollapsed ? "ChevronRight" : "ChevronLeft"}
              iconSize={16}
              className="text-muted-foreground hover:text-foreground"
            />
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1">
          {navigationItems?.map((item, index) => (
            <div
              key={item?.path}
              className="relative"
              onMouseEnter={() => handleItemHover(index)}
              onMouseLeave={handleItemLeave}
            >
              <Link
                to={item?.path}
                className={`flex items-center space-x-3 px-3 py-3 rounded-md text-sm font-medium transition-smooth group ${
                  isActivePath(item?.path)
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                } ${isCollapsed ? 'justify-center' : ''}`}
              >
                <Icon 
                  name={item?.icon} 
                  size={18} 
                  className={`flex-shrink-0 ${
                    isActivePath(item?.path) ? 'text-primary-foreground' : ''
                  }`}
                />
                {!isCollapsed && <span className="truncate">{item?.label}</span>}
              </Link>

              {/* Tooltip for collapsed state */}
              {isCollapsed && hoveredItem === index && (
                <div className="absolute left-full top-0 ml-2 px-3 py-2 bg-popover border border-border rounded-md shadow-elevation-lg z-50 whitespace-nowrap">
                  <div className="text-sm font-medium text-popover-foreground">{item?.label}</div>
                  <div className="text-xs text-muted-foreground mt-1">{item?.tooltip}</div>
                </div>
              )}
            </div>
          ))}
        </nav>
      </aside>
      {/* Content Spacer */}
      <div className={`transition-all duration-300 ${isCollapsed ? 'ml-16' : 'ml-60'}`} />
    </>
  );
};

export default Sidebar;